import { IPromise } from 'angular';
import { Messages, Formatters } from '../../codegen/Localize';
import { MetricDefinition } from '../../metrics/models/MetricDefinition';
import { ITagWithCount } from '../directives/tbTagsInput';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode, TagErrorCode } from '../helpers/ErrorCodes';
import { ResultHelper } from '../helpers/ResultHelper';
import { TagsHelper } from '../helpers/TagsInputHelper';
import { IContentAndLuidInfo } from '../mappers/DatabaseAndTableMapper';
import { DatasourceDefinition, FlowDefinition, PublishedConnectionDefinition, ViewDefinition, WorkbookDefinition } from '../models/ContentDefinitions';
import { FlowListItemInfo } from '../models/FlowInfo';
import { IContentInfo } from '../models/IContentInfo';
import { IDataSourceBase } from '../models/IDataSourceInfo';
import { IMetricInfo } from '../../metrics/models/IMetricInfo';
import { IPublishedConnectionInfo } from '../models/IPublishedConnectionInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IWorkbookBase } from '../models/IWorkbookInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import { INamedItemInfo, ITaggedItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { Databases } from '../resources/Databases';
import { DataSources } from '../resources/Datasources';
import { Flows } from '../resources/Flows';
import { Metrics } from '../../metrics/resources/Metrics';
import { PublishedConnections } from '../resources/PublishedConnections';
import { Tables } from '../resources/Tables';
import { Views } from '../../views/resources/Views';
import { Workbooks } from '../resources/Workbooks';
import { IResult } from '../ServerApi/types/IResponse';
import { ActionHelpers } from './ActionHelpers';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { IErrorDetail, INotificationTranslationFns, INotificationTranslationIds, actionSuccessTranslation, actionErrorTranslation } from './ContentActionNotification';
import { IConfirmDialogTagsScope } from './ContentActions';
import { DataSourceErrors } from './DataSourceErrors';
import { FlowErrors } from './FlowErrors';
import { MetricErrors } from '../../metrics/services/MetricErrors';
import { PublishedConnectionErrors } from './PublishedConnectionErrors';
import { ToasterService, ToastType } from './ToasterService';
import { ViewErrors } from './ViewErrors';
import { WorkbookErrors } from './WorkbookErrors';
import { RestApiErrorResponse } from './RestApiService';


export interface INamedAndTaggedItemInfo extends INamedItemInfo, ITaggedItemInfo { }

export interface IChangeTagsExecuteFnParams<T> {
  changeTagsAction: (ids: string[], addTags: string[], removeTags: string[]) => IPromise<IResult>;
  getDescription: (itemSet: Set<T>) => string;
  notificationFns: INotificationTranslationFns;
  errorDetailFn: (errorItem: IErrorItem<T>) => IErrorDetail;
}

export interface IChangeTagsExecuteFnForMixedContentParams<T> {
  getDescription: (itemSet: T[]) => string;
  notificationIds: INotificationTranslationIds;
}

export class ChangeTagsActionHelperService {

  public static $inject = [
    '$q',
    'ConfirmActionDialog',
    'DataSources',
    'DataSourceErrors',
    'ViewErrors',
    'Workbooks',
    'WorkbookErrors',
    'Flows',
    'FlowErrors',
    'PublishedConnections',
    'Databases',
    'Tables',
  ];

  constructor(
    private $q: ng.IQService,
    private ConfirmActionDialog: ConfirmActionDialog,
    private DataSources: DataSources,
    private DataSourceErrors: DataSourceErrors,
    private ViewErrors: ViewErrors,
    private Workbooks: Workbooks,
    private WorkbookErrors: WorkbookErrors,
    private Flows: Flows,
    private FlowErrors: FlowErrors,
    private PublishedConnections: PublishedConnections,
    private Databases: Databases,
    private Tables: Tables,
  ) { }

  public createExecuteFn<T extends INamedAndTaggedItemInfo>(params: IChangeTagsExecuteFnParams<T>) {
    return (itemSet: Set<T>, context: IActionContext) => {

      const scope = this.configureScope(context.scope, itemSet, params.getDescription);
      const previousTagSet = TagsHelper.tagSetFromTagsList(scope.tags.model);

      const dialogConfig = {
        scope: scope,
        bodyTemplateUrl: 'confirmActionDialogTags.html',
        title: Messages.changeTags(),
        actionName: Messages.changeTagsConfirm(),
        action: () => {
          const editTags = TagsHelper.getTagsChange(previousTagSet, scope.tags.model);
          return params.changeTagsAction(itemSet.keys(), editTags.addTags, editTags.removeTags).then(result => {
            return {
              ...result,
              tags: scope.tags.model.map(m => TagsHelper.unquote(m.name))
            };
          });
        },
        position: { reserveHeight: 450 }
      };

      const dialogInstance = this.ConfirmActionDialog.open(dialogConfig);

      dialogInstance.result
        .then(result =>
          ActionHelpers.notifyStandardError(
            itemSet,
            result,
            params.notificationFns,
            params.errorDetailFn
          )
        ).finally(() => scope.$destroy());

      return dialogInstance.result;
    };
  }

  public createExecuteFnForMixedContent(params: IChangeTagsExecuteFnForMixedContentParams<IContentInfo>) {
    return (items: IContentInfo[], context: IActionContext) => {
      const scope = this.configureScopeForMixedContent(context.scope, items, params.getDescription);
      const previousTagSet = TagsHelper.tagSetFromTagsList(scope.tags.model);

      const dialogConfig = {
        scope: scope,
        bodyTemplateUrl: 'confirmActionDialogTags.html',
        title: Messages.changeTags(),
        actionName: Messages.changeTagsConfirm(),
        action: () => this.changeTagsForContent(items, previousTagSet, scope),
        position: { reserveHeight: 450 }
      };

      const dialogInstance = this.ConfirmActionDialog.open(dialogConfig);

      dialogInstance.result
        .then((results: IResult[]) => ({
          ...ResultHelper.combineResults(results),
          tags: scope.tags.model.map(m => TagsHelper.unquote(m.name))
        }))
        .finally(() => scope.$destroy());

      return dialogInstance.result;
    };
  }

  private configureScope<T extends INamedAndTaggedItemInfo>(parentScope: ng.IScope, itemSet: Set<T>, getDescription: (itemSet: Set<T>) => string): IConfirmDialogTagsScope {
    const scope = <IConfirmDialogTagsScope>parentScope.$new();

    scope.description = getDescription(itemSet);

    scope.tags = {
      model: TagsHelper.tagsListFromTaggedItemSet(itemSet)
    };

    return scope;
  }

  private configureScopeForMixedContent<T>(parentScope: ng.IScope, items: T[], getDescription: (itemSet: T[]) => string): IConfirmDialogTagsScope {
    const scope = <IConfirmDialogTagsScope>parentScope.$new();

    scope.description = getDescription(items);

    scope.tags = {
      model: TagsHelper.tagsListFromTaggedItemArray(items)
    };

    return scope;
  }

  private changeTagsForContent(items: IContentInfo[], previousTagSet: Set<ITagWithCount>, scope: IConfirmDialogTagsScope): IPromise<IResult[]> {
    // We can only change the items to a set when we split by content type because different items of different content
    // types could have the same id. A set keyed on id would remove the 'duplicate' id, even though it corresponds to
    // a different item. Ids _are_ unique to content types so making them sets here is safe.
    const views = Set.fromIdItemArray(items.filter(i => i.contentType === ViewDefinition.contentType));
    const workbooks = Set.fromIdItemArray(items.filter(i => i.contentType === WorkbookDefinition.contentType));
    const datasources = Set.fromIdItemArray(items.filter(i => i.contentType === DatasourceDefinition.contentType));
    const flows = Set.fromIdItemArray(items.filter(i => i.contentType === FlowDefinition.contentType));
    const metrics =  Set.fromIdItemArray(items.filter(i => i.contentType === MetricDefinition.contentType));
    const publishedConnections =  Set.fromIdItemArray(items.filter(i => i.contentType === PublishedConnectionDefinition.contentType));
    const databases = Set.fromIdItemArray(items.filter(i => i.contentType === 'database'));
    const tables = Set.fromIdItemArray(items.filter(i => i.contentType === 'table'));

    // NOTE: If we ever allow catalog content to be part of mixed content selection,
    // we'll need to filter by content type then ensure luid is included on the filtered collection

    const editTags = TagsHelper.getTagsChange(previousTagSet, scope.tags.model);

    // Note (abrandon 2020-05-05) It appears that no code is currently using allResults. Can we remove it?
    const allResults: IResult[] = [];

    // We can't call these asynchronously because when adding a new tag, the backend creates an id for it. If
    // the calls are done at the same time, the server throws an error (NonUniqueResultException) about the
    // same tag getting two ids. Instead, we have to chain these calls and save the results from each one in an array.
    return this.changeTagsForWorkbooks(workbooks, editTags.addTags, editTags.removeTags)
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForFlows(flows, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForViews(views, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForDatasources(datasources, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForMetrics(metrics, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForPublishedConnections(publishedConnections, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForDatabases(databases, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return this.changeTagsForTables(tables, editTags.addTags, editTags.removeTags);
      })
      .then((result: IResult) => {
        allResults.push(result);
        return allResults;
      });
  }

  private changeTagsForViews(views: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (views.size() < 1) {
      return this.$q.when(null);
    }

    return Views.changeTags(views.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.notifyStandardError(
        views,
        result,
        {
          success: actionSuccessTranslation(Messages.changeTagsViewsSuccess),
          error: actionErrorTranslation(Messages.changeTagsViewsError),
        },
        (errorItem: IErrorItem<IViewInfo>) => this.ViewErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForFlows(flows: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (flows.size() < 1) {
      return this.$q.when(null);
    }

    return this.Flows.changeTags(flows.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.notifyStandardError(
        flows,
        result,
        {
          success: actionSuccessTranslation(Messages.changeTagsFlowsSuccess),
          error: actionErrorTranslation(Messages.changeTagsFlowsError),
        },
        (errorItem: IErrorItem<FlowListItemInfo>) => this.FlowErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForWorkbooks(workbooks: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (workbooks.size() < 1) {
      return this.$q.when(null);
    }

    return this.Workbooks.changeTags(workbooks.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.notifyStandardError(
        workbooks,
        result,
        {
          success: actionSuccessTranslation(Messages.changeTagsWorkbooksSuccess),
          error: actionErrorTranslation(Messages.changeTagsWorkbooksError),
        },
        (errorItem: IErrorItem<IWorkbookBase>) => this.WorkbookErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForDatasources(datasources: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (datasources.size() < 1) {
      return this.$q.when(null);
    }

    return this.DataSources.changeTags(datasources.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.notifyStandardError(
        datasources,
        result,
        {
          success: actionSuccessTranslation(Messages.changeTagsDatasourcesSuccess),
          error: actionErrorTranslation(Messages.changeTagsDatasourcesError),
        },
        (errorItem: IErrorItem<IDataSourceBase>) => this.DataSourceErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForMetrics(metrics: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (metrics.size() < 1) {
      return this.$q.when(null);
    }

    return Metrics.changeTags(metrics.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.notifyStandardError(
        metrics,
        result,
        {
          success: actionSuccessTranslation(Messages.changeTagsMetricsSuccess),
          error: actionErrorTranslation(Messages.changeTagsMetricsError),
        },
        (errorItem: IErrorItem<IMetricInfo>) => MetricErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForPublishedConnections(publishedConnections: Set<IContentInfo>, addTags: string[], removeTags: string[]): IPromise<IResult> {
    if (publishedConnections.size() < 1) {
      return this.$q.when(null);
    }

    return this.PublishedConnections.changeTags(publishedConnections.keys(), addTags, removeTags).then((result: IResult) => {
      return ActionHelpers.notifyStandardError(
        publishedConnections,
        result,
        {
          success: actionSuccessTranslation(Messages.changeTagsPublishedConnectionsSuccess),
          error: actionErrorTranslation(Messages.changeTagsPublishedConnectionsError),
        },
        (errorItem: IErrorItem<IPublishedConnectionInfo>) => PublishedConnectionErrors.errorDetailsFor(errorItem)
      );
    });
  }

  private changeTagsForDatabases(databases: Set<IContentAndLuidInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    const databasesArray = databases.toArray();
    if (databasesArray.length < 1) {
      return Promise.resolve(null);
    }

    // The REST APIs for external content give a single error message for the whole action,
    // rather than individual messages per content item.
    return this.Databases.changeTags({ databases: databasesArray, tagsToAdd: addTags, tagsToRemove: removeTags })
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: Messages.changeTagsDatabasesSuccess({
            ITEM_COUNT: databasesArray.length,
            ITEM_COUNT_LOC: Formatters.formatCount(databasesArray.length),
          })
        });
        return {};
      })
      .catch(e => {
        const errorResponse = e as RestApiErrorResponse;
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.changeTagsDatabasesError({
            ITEM_COUNT: databasesArray.length,
            ERROR_MESSAGE: errorResponse.data.error.detail,
          })
        });
        return {};
      });
  }

  private changeTagsForTables(tables: Set<IContentAndLuidInfo>, addTags: string[], removeTags: string[]): Promise<IResult> {
    const tablesArray = tables.toArray();
    if (tablesArray.length < 1) {
      return Promise.resolve(null);
    }

    // The REST APIs for external content give a single error message for the whole action,
    // rather than individual messages per content item.
    return this.Tables.changeTags({ tables: tablesArray, tagsToAdd: addTags, tagsToRemove: removeTags })
      .then(() => {
        ToasterService.instance.pop({
          type: ToastType.Info,
          text: Messages.changeTagsTablesSuccess({
            ITEM_COUNT: tablesArray.length,
            ITEM_COUNT_LOC: Formatters.formatCount(tablesArray.length),
          })
        });
        return {};
      })
      .catch(e => {
        const errorResponse = e as RestApiErrorResponse;
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.changeTagsTablesError({
            ITEM_COUNT: tablesArray.length,
            ERROR_MESSAGE: errorResponse.data.error.detail,
          })
        });
        return {};
      });
  }

  public errorMessageFor(errorItem: IErrorItem<any>): string {
    switch (errorItem.error.code) {
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.changeTagsErrorNoPermission();
      case TagErrorCode.TagNameTooLong:
        return Messages.changeTagsErrorNameTooLong();
      default:
        return Messages.errorUnknown();
    }
  }

}

Module.instance().service('ChangeTagsActionHelperService', ChangeTagsActionHelperService);
